import { InputRule, Node } from "@tiptap/core";
import { usePlainDocument } from "../services/editorSurface";
import {
  NodeViewWrapper,
  ReactNodeViewRenderer,
  type NodeViewProps,
} from "@tiptap/react";
import QueryInput, { type ToolOption } from "./QueryInput";
import { useEffect, useCallback, useMemo } from "react";
import React from "react";

const MemoizedQueryInput = React.memo(QueryInput);

const QueryInputNodeView = React.memo(
  ({
    node,
    updateAttributes,
  }: Pick<NodeViewProps, "node" | "updateAttributes">) => {
    const plainDocument = usePlainDocument();
    const {
      qid,
      mountReason,
      workspaceQids,
      selectedTool,
      isExtendedThinking,
    } = node.attrs;
    const handleUpdate = useCallback(() => {
      if (mountReason === "initialLoad") {
        updateAttributes({ mountReason: "typing" });
      }
    }, [mountReason, updateAttributes]);

    const handleToolChange = useCallback(
      (tool: ToolOption) => {
        updateAttributes({ selectedTool: tool });
      },
      [updateAttributes]
    );

    const handleExtendedThinkingChange = useCallback(
      (enabled: boolean) => {
        updateAttributes({ isExtendedThinking: enabled });
      },
      [updateAttributes]
    );

    useEffect(() => {
      handleUpdate();
    }, [handleUpdate, qid]);

    const memoizedProps = useMemo(
      () => ({
        qid,
        mountReason,
        workspaceQids,
        selectedTool,
        onToolChange: handleToolChange,
        isExtendedThinking,
        onExtendedThinkingChange: handleExtendedThinkingChange,
      }),
      [
        qid,
        mountReason,
        workspaceQids,
        selectedTool,
        handleToolChange,
        isExtendedThinking,
        handleExtendedThinkingChange,
      ]
    );

    // A Document is plain text (#11548 round 16): the stored node survives
    // (hiding, not deleting — reopening on the Reports surface restores it),
    // but no Ask-Finpilot box renders. The empty wrapper is safe because
    // the node is an atom — there is no contentDOM to provide.
    if (plainDocument) {
      return <NodeViewWrapper node={node} contentEditable={false} />;
    }

    return (
      <NodeViewWrapper
        node={node}
        contentEditable={false}
        data-testid="query-input-node"
      >
        <MemoizedQueryInput {...memoizedProps} />
        {/* <span>QUERY_INPUT_NODE</span> */}
      </NodeViewWrapper>
    );
  }
);

const QueryInputNode = Node.create({
  name: "queryInput",

  group: "block",

  atom: true,
  selectable: false,
  // `editable`, `contentEditable` and `shouldUpdate` used to sit here. None
  // is a NodeConfig member; v2's index signature swallowed them silently, so
  // they never took effect, and v3's typed config rejects them (#11904).

  addAttributes() {
    return {
      qid: {
        default: "",
      },
      mountReason: {
        default: "initialLoad",
      },
      workspaceQids: {
        default: "",
      },
      selectedTool: {
        default: null,
      },
      isExtendedThinking: {
        default: false,
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: "query-input",
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ["query-input", HTMLAttributes];
  },

  addNodeView() {
    return ReactNodeViewRenderer(QueryInputNodeView);
  },

  addInputRules() {
    return [
      // An InputRule instance, not a plain object. v3's NodeConfig types
      // `addInputRules` as returning InputRule[], so the literal this used
      // to return is a type error; the class also sets `undoable`, which the
      // literal left undefined (#11904).
      new InputRule({
        find: /\\queryInput$/,
        handler: () => {
          // Documents are plain text (#11548 round 16): the "/" gate's twin.
          // Inserting here would create an invisible node the user only
          // discovers reopening the workspace under /reports.
          if (this.editor.storage.fpPlainDocument) {
            return;
          }
          this.editor.commands.insertContent("<query-input></query-input>");
        },
      }),
    ];
  },
});

export default QueryInputNode;
